/**
 * Accua Cookie Consent - Accessible Modal Handler
 *
 * This script handles the modal for all positions with full
 * EU Accessibility Act and WCAG 2.1 AA compliance.
 *
 * Features:
 * - Focus trap (Tab/Shift+Tab cycles within modal)
 * - Escape key / X button dismiss the modal keeping the saved preferences
 *   (or the default settings on first visit) — unconfirmed changes are discarded
 * - Backdrop click triggers nudge animation (modal is persistent)
 * - Background content made inert while modal is open
 * - Focus management: initial focus on modal, restore on close
 * - Two-view system (compact ↔ detail) with checkbox sync
 *
 * Since 6.8.0 the dialog is in the page from the start and js/boot.js may have
 * shown it already, before this script arrived; init() takes over from there,
 * applying any click the visitor made in the meantime. The detail view is loaded
 * on demand (see loadDetails()). Plain JavaScript, no jQuery.
 *
 * Dependencies: scripts.js (AccuaCookieConsent)
 *
 * @since 5.0.0
 */
(function () {
    'use strict';

    var d = document;
    var CLOSING_ACTIONS = ['accua-modal-accept', 'accua-modal-confirm', 'accua-modal-close'];

    function byId(id) {
        return d.getElementById(id);
    }

    function on(el, type, handler) {
        if (el) {
            el.addEventListener(type, handler);
        }
    }

    function onReady(fn) {
        if (d.readyState === 'loading') {
            d.addEventListener('DOMContentLoaded', fn);
        } else {
            setTimeout(fn, 0);
        }
    }

    function modalData() {
        return (typeof window.accua_modal_data !== 'undefined') ? window.accua_modal_data : null;
    }

    function srText(key, fallback) {
        const data = modalData();
        return (data && data.sr && data.sr[key]) ? data.sr[key] : fallback;
    }

    /**
     * Accessible Modal Handler for all positions
     *
     * @namespace AccuaAccessibleModal
     */
    const AccuaAccessibleModal = {
        modal: null,
        backdrop: null,
        triggerElement: null,
        focusableElements: [],
        firstFocusable: null,
        lastFocusable: null,
        isOpen: false,

        // Privacy modal elements
        privacyModal: null,
        privacyBackdrop: null,
        privacyTrigger: null,
        isPrivacyOpen: false,
        privacyContentLoaded: false,

        // Detail view (loaded on demand since 6.8.0)
        detailsLoaded: false,
        detailsLoading: false,
        detailsWaiting: [],
        detailsPrefetch: false,

        /**
         * Initialize the accessible modal
         */
        init: function () {
            // Pages cached by an older version carry the dialog in per-language
            // <template> elements: clone the browser-language one first
            this.instantiateTemplate();

            const acc = (typeof AccuaCookieConsent !== 'undefined') ? AccuaCookieConsent : null;
            const boot = window.accuaCookieBoot || null;
            // From now on clicks reach the handlers below, not the bootstrap's queue
            const queue = boot ? boot.queue.splice(0) : [];
            if (boot) {
                boot.ready = true;
            }

            this.modal = byId('accua-modal-modal');
            this.backdrop = byId('accua-modal-backdrop');

            // Only initialize if the modal exists (cimatti_a11y position)
            if (!this.modal) {
                d.documentElement.classList.remove('accua-cc-preopen');
                return;
            }

            // Initialize privacy modal elements
            this.privacyModal = byId('accua-modal-privacy-modal');
            this.privacyBackdrop = byId('accua-modal-privacy-backdrop');

            this.setupEventListeners();
            this.setupMoreInfoToggle();
            this.setupPrivacyModal();
            this.setupOrientationHandler();

            let closing = null;
            queue.forEach(function (item) {
                if (!closing && CLOSING_ACTIONS.indexOf(item.id) !== -1) {
                    closing = item.id;
                }
            });

            // Show modal if no cookie exists, or if new categories appeared
            // since the last consent (re-prompt). AccuaCookieConsent.init()
            // runs before this init, so newSettingsPending is already set;
            // its own open() call happens too early (this.modal still null).
            // popupDisabled (the admin's per-URL list) suppresses only this
            // automatic open — open() itself stays available to the floating
            // icon and to [accua-cookie-dialog] links. Never for crawlers
            // with "skip bots" on.
            const shouldOpen = acc && !acc.isBot && !acc.popupDisabled &&
                (acc.currentCookieValue === null ||
                 acc.currentCookieValue === "" ||
                 acc.newSettingsPending);

            if (closing && acc && !acc.isBot) {
                // The visitor already answered while this script was loading;
                // the bootstrap hid the dialog at that moment.
                this.applyEarlyAnswer(closing);
            } else if (shouldOpen) {
                this.open();
                this.replayViewActions(queue);
            } else if (boot && boot.opened && boot.hide) {
                boot.hide();
            }

            d.documentElement.classList.remove('accua-cc-preopen');
        },

        /**
         * Safety net: normally AccuaCookieConsent.init() (scripts.js, runs
         * first) has already cloned the browser-language <template> into the
         * DOM (pages cached before 6.8.0); this covers the case where that
         * init did not run.
         */
        instantiateTemplate: function () {
            if (typeof AccuaCookieConsent !== 'undefined' && AccuaCookieConsent.instantiateModalTemplate) {
                AccuaCookieConsent.instantiateModalTemplate();
            }
        },

        /**
         * Apply an Accept / Confirm / Close the visitor clicked before this
         * script was ready (the dialog is already hidden).
         *
         * @param {string} id Id of the button (or 'accua-modal-close' for Escape)
         */
        applyEarlyAnswer: function (id) {
            if (id === 'accua-modal-accept') {
                this.checkAll();
                this.commitConfirm();
            } else if (id === 'accua-modal-confirm') {
                this.commitConfirm();
            } else {
                this.commitDismiss();
            }
            this.restoreFloatingIcon();
        },

        /**
         * Replay "More information", "?" and privacy-link clicks made before
         * this script was ready.
         *
         * @param {Array} queue Actions recorded by js/boot.js
         */
        replayViewActions: function (queue) {
            const self = this;
            queue.forEach(function (item) {
                if (item.id === 'accua-modal-more-link') {
                    const detailView = byId('accua-modal-detail-view');
                    if (detailView && !detailView.hidden) {
                        self.showCompactView();
                    } else {
                        self.showDetailView();
                    }
                } else if (item.id === 'accua-modal-privacy-link') {
                    self.privacyTrigger = byId('accua-modal-privacy-link');
                    self.openPrivacyModal();
                } else if (item.category) {
                    self.showCategoryDetail(item.category);
                }
            });
        },

        /** Tick every optional category (Accept All). */
        checkAll: function () {
            this.modal.querySelectorAll('input[type="checkbox"]:not(:disabled)').forEach(function (cb) {
                cb.checked = true;
            });
        },

        /**
         * Setup all event listeners for the modal
         */
        setupEventListeners: function () {
            const self = this;

            // Accept All button
            on(byId('accua-modal-accept'), 'click', function (e) {
                e.preventDefault();
                // Check all checkboxes
                self.checkAll();
                self.confirmAndClose();
            });

            // Confirm Selection button
            on(byId('accua-modal-confirm'), 'click', function (e) {
                e.preventDefault();
                self.confirmAndClose();
            });

            // Close (X) button - dismiss keeping saved/default settings
            on(byId('accua-modal-close'), 'click', function (e) {
                e.preventDefault();
                self.dismissAndClose();
            });

            // Backdrop click - nudge animation (don't close)
            on(this.backdrop, 'click', function (e) {
                e.preventDefault();
                self.nudge();
            });

            // Keyboard handling
            d.addEventListener('keydown', function (e) {
                if (!self.isOpen) return;

                // If privacy modal is open, don't handle keys for consent modal
                if (self.isPrivacyOpen) return;

                // Escape key - dismiss keeping saved/default settings (same as X)
                if (e.key === 'Escape' || e.keyCode === 27) {
                    e.preventDefault();
                    self.dismissAndClose();
                    return;
                }

                // Tab key - focus trap
                if (e.key === 'Tab' || e.keyCode === 9) {
                    self.handleTabKey(e);
                }
            });

            // Accept All hover/focus highlights unchecked checkboxes (if enabled)
            const data = modalData();
            if (data && data.acceptAllPreview === '1') {
                const accept = byId('accua-modal-accept');
                const highlight = function () {
                    self.highlightUncheckedCategories(true);
                };
                const unhighlight = function () {
                    self.highlightUncheckedCategories(false);
                };
                on(accept, 'mouseenter', highlight);
                on(accept, 'focus', highlight);
                on(accept, 'mouseleave', unhighlight);
                on(accept, 'blur', unhighlight);
            }
        },

        /**
         * Highlight unchecked category checkboxes (visual preview for Accept All)
         */
        highlightUncheckedCategories: function (highlight) {
            const unchecked = this.modal.querySelectorAll('.accua-modal-checkbox:not(:checked):not(:disabled)');
            unchecked.forEach(function (cb) {
                const category = cb.closest('.accua-modal-category');
                if (category) {
                    if (highlight) {
                        category.classList.add('accua-modal-will-check');
                    } else {
                        category.classList.remove('accua-modal-will-check');
                    }
                }
            });
        },

        /**
         * Setup the More Info link and info buttons for view switching
         */
        setupMoreInfoToggle: function () {
            const self = this;

            // "Maggiori informazioni" / "Indietro" toggle link in footer
            on(byId('accua-modal-more-link'), 'click', function (e) {
                e.preventDefault();
                const detailView = byId('accua-modal-detail-view');
                if (detailView && !detailView.hidden) {
                    self.showCompactView();
                } else {
                    self.showDetailView();
                }
            });

            // Setup info buttons (?) that switch to detail view and scroll to category
            this.modal.querySelectorAll('.accua-modal-info-btn').forEach(function (btn) {
                on(btn, 'click', function (e) {
                    e.preventDefault();
                    self.showCategoryDetail(btn.getAttribute('data-category'));
                });
            });

            // Sync checkboxes between compact and detail views
            this.setupCheckboxSync();
        },

        /**
         * Setup checkbox synchronization between footer checkboxes and detail view
         *
         * The footer checkboxes (in .accua-modal-categories) are always visible,
         * while the detail view checkboxes are inside #accua-modal-detail-view.
         * We need to sync between these two locations.
         *
         * OPTIMIZED: Uses native DOM APIs and pre-built checkbox map for O(1) lookups
         */
        setupCheckboxSync: function () {
            const self = this;
            const categoriesEl = d.querySelector('.accua-modal-categories');
            const detailViewEl = byId('accua-modal-detail-view');

            // Pre-build checkbox maps for O(1) lookup instead of DOM queries.
            // Rebuilt when the detail view arrives (loadDetails()).
            this.refreshCheckboxMap();

            // Sync function using cached references (no DOM queries)
            function syncCheckbox(source, isFromCompact) {
                const name = source.name || source.value;
                if (!name) return;

                const target = isFromCompact ? self.checkboxMap.detail[name] : self.checkboxMap.compact[name];
                if (target && !target.disabled) {
                    target.checked = source.checked;
                }
            }

            // Event delegation with native DOM (also covers detail content
            // inserted later)
            if (categoriesEl) {
                categoriesEl.addEventListener('change', function(e) {
                    if (e.target.classList.contains('accua-modal-checkbox')) {
                        syncCheckbox(e.target, true);
                    }
                });

                // Row click to toggle checkbox
                categoriesEl.addEventListener('click', function(e) {
                    const category = e.target.closest('.accua-modal-category');
                    if (!category) return;
                    // Don't toggle if clicking checkbox, info button, or label (label[for] already toggles natively)
                    if (e.target.closest('.accua-modal-checkbox, .accua-modal-info-btn, label[for]')) return;

                    const checkbox = category.querySelector('.accua-modal-checkbox');
                    if (checkbox && !checkbox.disabled) {
                        checkbox.checked = !checkbox.checked;
                        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            }

            if (detailViewEl) {
                detailViewEl.addEventListener('change', function(e) {
                    if (e.target.classList.contains('accua-modal-checkbox')) {
                        syncCheckbox(e.target, false);
                    }
                });

                // Row click to toggle checkbox in detail view
                detailViewEl.addEventListener('click', function(e) {
                    const header = e.target.closest('.accua-modal-detail-header');
                    if (!header) return;
                    if (e.target.closest('.accua-modal-checkbox, label[for]')) return;

                    const checkbox = header.querySelector('.accua-modal-checkbox');
                    if (checkbox && !checkbox.disabled) {
                        checkbox.checked = !checkbox.checked;
                        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            }
        },

        /**
         * (Re)build the name => checkbox maps of both views.
         */
        refreshCheckboxMap: function () {
            const self = this;
            const categoriesEl = d.querySelector('.accua-modal-categories');
            const detailViewEl = byId('accua-modal-detail-view');
            this.checkboxMap = { compact: {}, detail: {} };

            if (categoriesEl) {
                categoriesEl.querySelectorAll('.accua-modal-checkbox').forEach(function(cb) {
                    const name = cb.name || cb.value;
                    if (name) self.checkboxMap.compact[name] = cb;
                });
            }

            if (detailViewEl) {
                detailViewEl.querySelectorAll('.accua-modal-checkbox').forEach(function(cb) {
                    const name = cb.name || cb.value;
                    if (name) self.checkboxMap.detail[name] = cb;
                });
            }
        },

        /**
         * Load the detail view (category descriptions and cookie tables) of the
         * visitor's language, then call back.
         *
         * Since 6.8.0 it is not in the page: it is most of the dialog's weight
         * and most visitors never open it. It comes with the language pack
         * (one static file per language, see js/boot.js); for a visitor whose
         * browser language differs from the page's, that pack is already
         * loaded. Pages cached by an older version still carry it inline:
         * nothing to load there.
         *
         * @param {Function} [callback] Called with true once inserted, false on failure
         */
        loadDetails: function (callback) {
            const self = this;
            const container = this.modal ? this.modal.querySelector('#accua-modal-detail-view .accua-modal-category-details') : null;
            const boot = window.accuaCookieBoot;

            if (!container || !boot || !boot.pack || this.detailsLoaded) {
                this.detailsLoaded = true;
                if (callback) callback(true);
                return;
            }

            this.detailsWaiting.push(callback);
            if (this.detailsLoading) {
                return;
            }
            this.detailsLoading = true;

            boot.pack(boot.lang, function (pack) {
                const ok = !!(pack && typeof pack.html === 'string');
                if (ok) {
                    self.insertDetails(container, pack.html);
                }
                self.detailsLoading = false;
                const waiting = self.detailsWaiting;
                self.detailsWaiting = [];
                waiting.forEach(function (cb) {
                    if (cb) cb(ok);
                });
            });
        },

        /**
         * Put the loaded detail view in place, with every checkbox matching
         * the compact view (which is what the visitor sees and changes).
         */
        insertDetails: function (container, html) {
            const self = this;
            container.innerHTML = html;
            container.removeAttribute('aria-busy');
            this.detailsLoaded = true;
            this.refreshCheckboxMap();
            Object.keys(this.checkboxMap.detail).forEach(function (name) {
                const source = self.checkboxMap.compact[name];
                const target = self.checkboxMap.detail[name];
                if (source && target && !target.disabled) {
                    target.checked = source.checked;
                }
            });
            // The focus trap needs no refresh: handleTabKey() rebuilds its list
        },

        /**
         * Fetch the detail view in the background once the page has loaded,
         * so "More information" opens instantly.
         */
        prefetchDetails: function () {
            const self = this;
            if (this.detailsLoaded || this.detailsPrefetch) {
                return;
            }
            this.detailsPrefetch = true;
            const go = function () {
                const idle = window.requestIdleCallback || function (fn) {
                    return setTimeout(fn, 200);
                };
                idle(function () {
                    self.loadDetails();
                }, { timeout: 3000 });
            };
            if (d.readyState === 'complete') {
                go();
            } else {
                window.addEventListener('load', go);
            }
        },

        /**
         * Show the detail view (full descriptions with checkboxes)
         */
        showDetailView: function (scrollToCategory) {
            const self = this;
            const compactView = byId('accua-modal-compact-view');
            const detailView = byId('accua-modal-detail-view');
            const moreLink = byId('accua-modal-more-link');

            if (!compactView || !detailView) return;

            // Not loaded yet (prefetch still running, or it failed): switch
            // anyway and fill the view when the content arrives.
            let pendingScroll = false;
            if (!this.detailsLoaded) {
                const container = detailView.querySelector('.accua-modal-category-details');
                const data = modalData();
                if (container) {
                    container.setAttribute('aria-busy', 'true');
                    container.innerHTML = '<p class="accua-modal-details-status">' +
                        ((data && data.loadingText) || 'Loading...') + '</p>';
                }
                pendingScroll = true;
                this.loadDetails(function (ok) {
                    if (!ok && container) {
                        container.removeAttribute('aria-busy');
                        container.innerHTML = '<p class="accua-modal-details-status">' +
                            ((data && data.errorText) || 'Error loading content.') + '</p>';
                        return;
                    }
                    if (scrollToCategory && !detailView.hidden) {
                        self.scrollToCategory(scrollToCategory);
                    }
                });
            }

            // Use native classList for better performance
            compactView.classList.add('accua-modal-view-exiting');

            // Reduced delay for snappier transitions (50ms is enough for crossfade)
            setTimeout(function () {
                compactView.hidden = true;
                compactView.classList.remove('accua-modal-view-active', 'accua-modal-view-exiting');

                detailView.hidden = false;
                detailView.classList.add('accua-modal-view-active');

                // Add class to modal wrapper to indicate detail view is active
                self.modal.classList.add('accua-modal-detail-active');

                // Update link to show "Indietro" with back arrow
                if (moreLink) {
                    const backText = moreLink.getAttribute('data-back-text') || 'Indietro';
                    const textSpan = moreLink.querySelector('.accua-modal-link-text');
                    const iconSpan = moreLink.querySelector('.accua-modal-back-icon');
                    if (textSpan) textSpan.textContent = backText;
                    if (iconSpan) iconSpan.textContent = '← ';
                    moreLink.classList.add('accua-modal-showing-back');
                    moreLink.focus();
                }

                // Scroll to category immediately (no delay needed)
                if (scrollToCategory && (!pendingScroll || self.detailsLoaded)) {
                    self.scrollToCategory(scrollToCategory);
                }

                // Update focusable elements
                self.updateFocusableElements();

                // Announce to screen readers
                self.announce(srText('showDetail', 'Showing detailed cookie information'));
            }, 50);
        },

        /**
         * Show the compact view (checkboxes only)
         */
        showCompactView: function () {
            const self = this;
            const compactView = byId('accua-modal-compact-view');
            const detailView = byId('accua-modal-detail-view');
            const moreLink = byId('accua-modal-more-link');

            if (!compactView || !detailView) return;

            // Use native classList for better performance
            detailView.classList.add('accua-modal-view-exiting');

            // Reduced delay for snappier transitions (50ms is enough for crossfade)
            setTimeout(function () {
                detailView.hidden = true;
                detailView.classList.remove('accua-modal-view-active', 'accua-modal-view-exiting');

                compactView.hidden = false;
                compactView.classList.add('accua-modal-view-active');

                // Remove class from modal wrapper when returning to compact view
                self.modal.classList.remove('accua-modal-detail-active');

                // Update link to show "Maggiori informazioni" again
                if (moreLink) {
                    const moreText = moreLink.getAttribute('data-more-text') || 'Maggiori informazioni';
                    const textSpan = moreLink.querySelector('.accua-modal-link-text');
                    const iconSpan = moreLink.querySelector('.accua-modal-back-icon');
                    if (textSpan) textSpan.textContent = moreText;
                    if (iconSpan) iconSpan.textContent = '';
                    moreLink.classList.remove('accua-modal-showing-back');
                    moreLink.focus();
                }

                // Update focusable elements
                self.updateFocusableElements();

                // Announce to screen readers
                self.announce(srText('showCompact', 'Returned to cookie preferences'));
            }, 50);
        },

        /**
         * Scroll to a specific category in the detail view
         */
        scrollToCategory: function (categoryName) {
            const targetId = 'accua-modal-detail-' + categoryName;
            const targetSection = byId(targetId);

            if (!targetSection) return;

            // Scroll the section into view
            targetSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

            // Add highlight animation
            targetSection.classList.add('accua-modal-highlight');

            // Remove highlight class after animation completes
            setTimeout(function () {
                targetSection.classList.remove('accua-modal-highlight');
            }, 1100);
        },

        /**
         * Show a specific category's detail section (from ? button click)
         */
        showCategoryDetail: function (categoryName) {
            const detailView = byId('accua-modal-detail-view');

            // If already in detail view, just scroll to category
            if (detailView && !detailView.hidden) {
                this.scrollToCategory(categoryName);
            } else {
                // Switch to detail view and scroll to category
                this.showDetailView(categoryName);
            }
        },

        /**
         * Open the modal
         */
        open: function () {
            // Guard: if modal doesn't exist (e.g., admin users), do nothing
            if (!this.modal) return;

            if (this.isOpen) return;

            this.isOpen = true;
            this.triggerElement = d.activeElement;

            // Hide floating icon while modal is open
            if (typeof AccuaCookieConsent !== 'undefined' && AccuaCookieConsent.hideFloatingIcon) {
                AccuaCookieConsent.hideFloatingIcon();
            }

            // Show modal and backdrop
            if (this.backdrop) {
                this.backdrop.classList.add('accua-modal-active');
            }
            this.modal.classList.add('accua-modal-active');

            // The focus trap's list of focusable elements is rebuilt on every
            // Tab (handleTabKey()). Building it here too only forced a layout
            // of the whole page (offsetParent) inside this script's task, a
            // long task on heavy pages when the dialog opens at page load.

            // Set initial focus on the modal itself, then move to first interactive element.
            // Before the background turns inert, and without scrolling (the
            // dialog is fixed to the viewport, there is nothing to scroll
            // to): focus() brings the styles up to date, and after the inert
            // change that meant restyling the whole page inside this
            // script's task, a long task on heavy pages at page load. The
            // next frame does it instead, as it would anyway.
            this.modal.focus({ preventScroll: true });

            // Make background inert
            this.setBackgroundInert(true);

            // Announce to screen readers
            this.announce(srText('dialogOpened', 'Cookie consent dialog opened'));

            // Have "More information" ready by the time it is clicked
            this.prefetchDetails();
        },

        /**
         * Close the modal
         */
        close: function () {
            if (!this.modal || !this.isOpen) return;

            this.isOpen = false;

            // Hide modal and backdrop
            this.modal.classList.remove('accua-modal-active');
            if (this.backdrop) {
                this.backdrop.classList.remove('accua-modal-active');
            }

            // Restore background
            this.setBackgroundInert(false);

            // Show floating icon again (with slight delay for animation)
            const self = this;
            setTimeout(function () {
                self.restoreFloatingIcon();
            }, 150);

            // Restore focus to trigger element
            if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
                this.triggerElement.focus();
            }
        },

        /**
         * Bring the floating icon back once the dialog is gone.
         */
        restoreFloatingIcon: function () {
            if (typeof AccuaCookieConsent !== 'undefined' && AccuaCookieConsent.showFloatingIcon) {
                AccuaCookieConsent.showFloatingIcon();
            }
            // Also un-hide if it was hidden for modal open
            d.querySelectorAll('.accua-cookie-floating-icon').forEach(function (icon) {
                if (!icon.classList.contains('accua-cookie-floating-icon--device-hidden')) {
                    icon.classList.remove('accua-cookie-floating-icon--hidden');
                }
            });
        },

        /**
         * Save the current selection (Confirm Selection / Accept All).
         */
        commitConfirm: function () {
            // Use the existing consent processing from AccuaCookieConsent
            if (typeof AccuaCookieConsent !== 'undefined') {
                AccuaCookieConsent.processConsent(true, "");
            }
        },

        /**
         * Discard unconfirmed changes (X / Escape); see dismissAndClose().
         */
        commitDismiss: function () {
            const acc = (typeof AccuaCookieConsent !== 'undefined') ? AccuaCookieConsent : null;

            this.resetSelectionsToSaved();

            if (acc) {
                const hasConsent = acc.currentCookieValue !== null && acc.currentCookieValue !== "";
                if (!hasConsent || acc.newSettingsPending) {
                    acc.processConsent(true, "");
                }
            }
        },

        /**
         * Confirm selection and close modal
         */
        confirmAndClose: function () {
            this.commitConfirm();

            // Announce to screen readers
            this.announce(srText('prefsSaved', 'Cookie preferences saved'));

            // Close the modal
            this.close();
        },

        /**
         * Dismiss the modal keeping the saved preferences (X button / Escape)
         *
         * Unconfirmed checkbox changes are discarded. On first visit (no
         * consent cookie yet) the checkboxes reset to the category defaults
         * and the cookie IS written with those defaults, so the modal does
         * not reappear. When a valid consent already exists the cookie is
         * NOT rewritten — unless new categories appeared since the last
         * consent (re-prompt), where the cookie must be completed with the
         * defaults of the new categories or the modal would reopen on every
         * page.
         */
        dismissAndClose: function () {
            this.commitDismiss();

            // Announce to screen readers
            this.announce(srText('closeDefaults', 'Close keeping the default settings'));

            // Close the modal
            this.close();
        },

        /**
         * Reset all category checkboxes (compact + detail view) to the saved
         * preferences, falling back to the category defaults for categories
         * without a saved preference (first visit or newly added categories).
         */
        resetSelectionsToSaved: function () {
            const acc = (typeof AccuaCookieConsent !== 'undefined') ? AccuaCookieConsent : null;
            if (!acc || !this.checkboxMap) return;

            const hasConsent = acc.currentCookieValue !== null && acc.currentCookieValue !== "";

            const self = this;
            ['compact', 'detail'].forEach(function (view) {
                const map = self.checkboxMap[view];
                if (!map) return;

                Object.keys(map).forEach(function (name) {
                    const cb = map[name];
                    if (!cb || cb.disabled) return;

                    if (hasConsent && typeof acc.preferences[name] !== 'undefined') {
                        cb.checked = acc.preferences[name] === true;
                    } else {
                        cb.checked = acc.getDefaultChecked(name);
                    }
                });
            });
        },

        /**
         * Trigger nudge animation when backdrop is clicked
         */
        nudge: function () {
            if (!this.modal) return;

            const self = this;

            // Add nudge class
            this.modal.classList.add('accua-modal-nudge');

            // Remove class after animation
            setTimeout(function () {
                self.modal.classList.remove('accua-modal-nudge');
            }, 300);

            // Focus the modal to draw attention
            this.modal.focus();
        },

        /**
         * Handle Tab key for focus trap
         */
        handleTabKey: function (e) {
            this.updateFocusableElements();

            if (this.focusableElements.length === 0) return;

            const activeElement = d.activeElement;

            if (e.shiftKey) {
                // Shift+Tab: if on first element, go to last
                if (activeElement === this.firstFocusable || activeElement === this.modal) {
                    e.preventDefault();
                    this.lastFocusable.focus();
                }
            } else {
                // Tab: if on last element, go to first
                if (activeElement === this.lastFocusable) {
                    e.preventDefault();
                    this.firstFocusable.focus();
                }
            }
        },

        /**
         * Update the list of focusable elements within the modal
         */
        updateFocusableElements: function () {
            if (!this.modal) return;

            const focusableSelectors = [
                'button:not([disabled])',
                'input:not([disabled])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                'a[href]',
                '[tabindex]:not([tabindex="-1"])'
            ].join(', ');

            this.focusableElements = Array.from(
                this.modal.querySelectorAll(focusableSelectors)
            ).filter(function (el) {
                return el.offsetParent !== null; // Visible elements only
            });

            this.firstFocusable = this.focusableElements[0] || null;
            this.lastFocusable = this.focusableElements[this.focusableElements.length - 1] || null;
        },

        /**
         * Set background content as inert (non-interactive)
         */
        setBackgroundInert: function (inert) {
            // Get all direct children of body except the modal, backdrop, and WP admin bar
            const bodyChildren = d.body.children;

            for (let i = 0; i < bodyChildren.length; i++) {
                const child = bodyChildren[i];

                // Skip the modal, backdrop, live region, privacy modal, and WP admin bar
                if (child === this.modal ||
                    child === this.backdrop ||
                    child === this.privacyModal ||
                    child === this.privacyBackdrop ||
                    child.id === 'accua-modal-announcer' ||
                    child.id === 'wpadminbar') {
                    continue;
                }

                if (inert) {
                    // Store original values
                    child.dataset.gdprOriginalAriaHidden = child.getAttribute('aria-hidden') || '';
                    child.dataset.gdprOriginalInert = child.hasAttribute('inert') ? 'true' : 'false';

                    // Make inert
                    child.setAttribute('aria-hidden', 'true');
                    child.setAttribute('inert', '');
                } else {
                    // Restore original values
                    const originalAriaHidden = child.dataset.gdprOriginalAriaHidden;
                    const originalInert = child.dataset.gdprOriginalInert;

                    // Elements added while the dialog was open carry no record
                    if (typeof originalAriaHidden === 'undefined') {
                        continue;
                    }

                    if (originalAriaHidden === '') {
                        child.removeAttribute('aria-hidden');
                    } else {
                        child.setAttribute('aria-hidden', originalAriaHidden);
                    }

                    if (originalInert !== 'true') {
                        child.removeAttribute('inert');
                    }

                    // Clean up data attributes
                    delete child.dataset.gdprOriginalAriaHidden;
                    delete child.dataset.gdprOriginalInert;
                }
            }
        },

        /**
         * Announce message to screen readers via live region
         */
        announce: function (message) {
            const announcer = byId('accua-modal-announcer');
            if (announcer) {
                announcer.textContent = message;
                // Clear after announcement
                setTimeout(function () {
                    announcer.textContent = '';
                }, 1000);
            }
        },

        /**
         * Setup privacy modal event listeners
         */
        setupPrivacyModal: function () {
            const self = this;
            const data = modalData();

            // If privacy opens in new tab, the link is a regular <a> — no JS needed
            if (data && data.privacyMode === 'newtab') {
                return;
            }

            // Privacy link in main modal
            on(byId('accua-modal-privacy-link'), 'click', function (e) {
                e.preventDefault();
                self.privacyTrigger = this;
                self.openPrivacyModal();
            });

            // Pages cached before 6.8.0 carry the privacy modal; newer ones
            // get it from the language pack on first use (ensurePrivacyModal())
            if (this.privacyModal) {
                this.bindPrivacyModal();
            }
        },

        /**
         * Add the privacy modal to the page (it comes with the language pack
         * since 6.8.0) and call back with true once it is there.
         */
        ensurePrivacyModal: function (callback) {
            const self = this;
            const boot = window.accuaCookieBoot;
            if (this.privacyModal) {
                callback(true);
                return;
            }
            if (!boot || !boot.pack || this.privacyLoading) {
                return;
            }
            this.privacyLoading = true;
            boot.pack(boot.lang, function (pack) {
                self.privacyLoading = false;
                if (!self.privacyModal && pack && pack.privacy) {
                    d.body.insertAdjacentHTML('beforeend', pack.privacy);
                    self.privacyModal = byId('accua-modal-privacy-modal');
                    self.privacyBackdrop = byId('accua-modal-privacy-backdrop');
                    if (self.privacyModal) {
                        self.bindPrivacyModal();
                    }
                }
                callback(!!self.privacyModal);
            });
        },

        /**
         * Listeners of the privacy modal (close, back, backdrop, keyboard).
         */
        bindPrivacyModal: function () {
            const self = this;

            // Close button
            on(byId('accua-modal-privacy-close'), 'click', function (e) {
                e.preventDefault();
                self.closePrivacyModal();
            });

            // Back button in footer
            on(byId('accua-modal-privacy-back'), 'click', function (e) {
                e.preventDefault();
                self.closePrivacyModal();
            });

            // Backdrop click - close privacy modal (it's informational, not required)
            on(this.privacyBackdrop, 'click', function (e) {
                e.preventDefault();
                self.closePrivacyModal();
            });

            // Keyboard handling for privacy modal
            d.addEventListener('keydown', function (e) {
                if (!self.isPrivacyOpen) return;

                // Escape key - close privacy modal
                if (e.key === 'Escape' || e.keyCode === 27) {
                    e.preventDefault();
                    e.stopPropagation();
                    self.closePrivacyModal();
                    return;
                }

                // Tab key - focus trap within privacy modal
                if (e.key === 'Tab' || e.keyCode === 9) {
                    self.handlePrivacyTabKey(e);
                }
            });
        },

        /**
         * Open the privacy policy modal
         */
        openPrivacyModal: function () {
            const self = this;
            if (this.isPrivacyOpen) return;
            if (!this.privacyModal) {
                this.ensurePrivacyModal(function (ok) {
                    if (ok) {
                        self.openPrivacyModal();
                    }
                });
                return;
            }

            this.isPrivacyOpen = true;

            // Remove inert/aria-hidden from privacy modal (may have been set by consent modal)
            this.privacyModal.removeAttribute('inert');
            this.privacyModal.removeAttribute('aria-hidden');
            this.privacyBackdrop.removeAttribute('inert');
            this.privacyBackdrop.removeAttribute('aria-hidden');

            // Show privacy backdrop and modal
            this.privacyBackdrop.classList.add('accua-modal-active');
            this.privacyModal.removeAttribute('hidden');
            this.privacyModal.classList.add('accua-modal-active');

            // Make consent modal and its backdrop inert while privacy is open
            if (this.modal) {
                this.modal.setAttribute('inert', '');
                this.modal.setAttribute('aria-hidden', 'true');
            }
            if (this.backdrop) {
                // Hide consent backdrop so it doesn't intercept clicks
                this.backdrop.classList.add('accua-modal-behind-privacy');
            }

            // Load content if not already loaded
            if (!this.privacyContentLoaded) {
                this.loadPrivacyContent();
            }

            // Focus the modal
            this.privacyModal.focus();

            // Announce to screen readers
            this.announce(srText('privacyOpened', 'Privacy policy opened'));
        },

        /**
         * Close the privacy policy modal
         */
        closePrivacyModal: function () {
            if (!this.isPrivacyOpen) return;

            this.isPrivacyOpen = false;

            // Hide privacy backdrop and modal
            this.privacyBackdrop.setAttribute('aria-hidden', 'true');
            this.privacyBackdrop.classList.remove('accua-modal-active');
            this.privacyModal.setAttribute('hidden', '');
            this.privacyModal.classList.remove('accua-modal-active');

            // Restore consent modal and its backdrop
            if (this.modal) {
                this.modal.removeAttribute('inert');
                this.modal.removeAttribute('aria-hidden');
            }
            if (this.backdrop) {
                this.backdrop.classList.remove('accua-modal-behind-privacy');
            }

            // Return focus to trigger element
            if (this.privacyTrigger && typeof this.privacyTrigger.focus === 'function') {
                this.privacyTrigger.focus();
            }

            // Announce to screen readers
            this.announce(srText('backToConsent', 'Returned to cookie consent'));
        },

        /**
         * Load privacy policy content via AJAX
         */
        loadPrivacyContent: function () {
            const self = this;
            const loadingEl = byId('accua-modal-privacy-loading');
            const textEl = byId('accua-modal-privacy-text');
            const titleEl = byId('accua-modal-privacy-title');
            const data = modalData();

            if (!textEl) return;

            // Check if accua_modal_data is available
            if (!data) {
                textEl.innerHTML = '<p>Error: Configuration not available.</p>';
                if (loadingEl) loadingEl.hidden = true;
                return;
            }

            // Show loading state
            if (loadingEl) loadingEl.hidden = false;
            textEl.innerHTML = '';

            // Check if this is an iframe-based privacy modal (same-domain custom URL)
            var iframeUrl = data.privacyIframeUrl || '';
            var privacyLink = byId('accua-modal-privacy-link');
            if (privacyLink && privacyLink.getAttribute('data-privacy-iframe')) {
                iframeUrl = privacyLink.getAttribute('data-privacy-iframe');
            }

            if (iframeUrl) {
                // Load content via iframe
                var bodyEl = byId('accua-modal-privacy-body');
                if (bodyEl) bodyEl.classList.add('accua-modal-privacy-body--iframe');

                var iframe = d.createElement('iframe');
                iframe.src = iframeUrl;
                iframe.className = 'accua-modal-privacy-iframe';
                iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups');
                iframe.setAttribute('title', titleEl ? titleEl.textContent : 'Privacy Policy');
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.border = 'none';
                iframe.onload = function () {
                    if (loadingEl) loadingEl.hidden = true;
                    self.privacyContentLoaded = true;
                };
                iframe.onerror = function () {
                    if (loadingEl) loadingEl.hidden = true;
                    textEl.innerHTML = '<p>' + (data.errorText || 'Error loading content.') + '</p>';
                };
                textEl.appendChild(iframe);
                return;
            }

            const showError = function () {
                if (loadingEl) loadingEl.hidden = true;
                textEl.innerHTML = '<p>' + (data.errorText || 'Error loading content.') + '</p>';
            };

            // AJAX request (WordPress page source)
            const body = new URLSearchParams();
            body.append('action', 'accua_get_privacy_content');
            body.append('lang', data.lang || '');

            fetch(data.ajaxUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: body.toString()
            }).then(function (res) {
                if (!res.ok) {
                    throw new Error('HTTP ' + res.status);
                }
                return res.json();
            }).then(function (response) {
                if (loadingEl) loadingEl.hidden = true;

                if (response && response.success && response.data) {
                    // Update title if provided
                    if (response.data.title && titleEl) {
                        titleEl.textContent = response.data.title;
                    }
                    // Insert content
                    textEl.innerHTML = response.data.content || '';
                    self.privacyContentLoaded = true;
                } else {
                    showError();
                }
            }).catch(showError);
        },

        /**
         * Handle Tab key for privacy modal focus trap
         */
        handlePrivacyTabKey: function (e) {
            const focusableSelectors = [
                'button:not([disabled])',
                'a[href]',
                '[tabindex]:not([tabindex="-1"])'
            ].join(', ');

            const focusableElements = Array.from(
                this.privacyModal.querySelectorAll(focusableSelectors)
            ).filter(function (el) {
                return el.offsetParent !== null;
            });

            if (focusableElements.length === 0) return;

            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];
            const activeElement = d.activeElement;

            if (e.shiftKey) {
                // Shift+Tab: if on first element, go to last
                if (activeElement === firstFocusable || activeElement === this.privacyModal) {
                    e.preventDefault();
                    lastFocusable.focus();
                }
            } else {
                // Tab: if on last element, go to first
                if (activeElement === lastFocusable) {
                    e.preventDefault();
                    firstFocusable.focus();
                }
            }
        },

        /**
         * Setup orientation and resize handlers for responsive layout
         */
        setupOrientationHandler: function () {
            const self = this;
            let resizeTimeout;

            // Debounced resize handler
            function handleResize() {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(function () {
                    self.updateOrientationClass();
                    self.updateFocusableElements();
                }, 100);
            }

            // Add orientation class on init, with the next frame: reading the
            // viewport size forces a layout of the whole page, which the frame
            // does anyway, while here it would land in this script's task. The
            // plugin's own CSS does not use these classes; they are hooks for
            // themes.
            requestAnimationFrame(function () {
                self.updateOrientationClass();
            });

            // Listen for resize (covers orientation changes)
            window.addEventListener('resize', handleResize, { passive: true });
        },

        /**
         * Update orientation class on modal for CSS hooks
         */
        updateOrientationClass: function () {
            if (!this.modal) return;

            const isLandscape = window.innerWidth > window.innerHeight;
            const isCramped = window.innerHeight <= 500;

            this.modal.classList.toggle('accua-modal-landscape', isLandscape);
            this.modal.classList.toggle('accua-modal-cramped', isCramped);
        }
    };

    // Expose to global scope for use by other scripts
    window.AccuaAccessibleModal = AccuaAccessibleModal;
    // Legacy alias for backwards compatibility
    window.GDPRAccessibleModal = AccuaAccessibleModal;

    // Initialize on document ready, after scripts.js (same queue, registered later)
    onReady(function () {
        const boot = window.accuaCookieBoot;
        if (boot && boot.whenReady) {
            boot.whenReady(function () {
                AccuaAccessibleModal.init();
            });
        } else {
            AccuaAccessibleModal.init();
        }
    });

})();
